import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { oltService, OLT } from '../../services/oltService';

interface OLTFormProps {
  onSuccess?: (olt: OLT) => void;
  onCancel?: () => void;
  olt?: OLT; // Para edição
}

interface OLTFormData {
  name: string;
  vendor: 'vsolution' | 'huawei' | 'datacom' | 'fdata' | '';
  model: string;
  ip_address: string;
  ssh_port: number;
  ssh_username: string;
  ssh_password: string;
  snmp_community: string;
  snmp_port: number;
  tipo_comunicacao: 'ssh' | 'telnet';
  porta_telnet: number;
  location: string;
  description: string;
  onu_profile: string;
  ativo: boolean;
  solicita_senha_enable: boolean;
  senha_enable: string;
}

const OLTForm: React.FC<OLTFormProps> = ({ onSuccess, onCancel, olt }) => {
  const [formData, setFormData] = useState<OLTFormData>({
    name: olt?.name || '',
    vendor: olt?.vendor || '',
    model: olt?.model || '',
    ip_address: olt?.ip_address || '',
    ssh_port: olt?.ssh_port || 22,
    ssh_username: olt?.ssh_username || '',
    ssh_password: olt?.ssh_password || '',
    snmp_community: olt?.snmp_community || 'public',
    snmp_port: olt?.snmp_port || 161,
    tipo_comunicacao: olt?.tipo_comunicacao || 'ssh',
    porta_telnet: olt?.porta_telnet || 23,
    location: olt?.location || '',
    description: olt?.description || '',
    onu_profile: olt?.onu_profile || '',
    ativo: olt?.ativo !== undefined ? olt.ativo : true,
    solicita_senha_enable: olt?.solicita_senha_enable || false,
    senha_enable: olt?.senha_enable || '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showEnablePassword, setShowEnablePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof OLTFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'ssh_port' || field === 'snmp_port' || field === 'porta_telnet' ? Number(value) : value
    }));
  };

  const handleSelectChange = (field: keyof OLTFormData) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.vendor) {
      setError('Fabricante é obrigatório');
      return false;
    }
    if (!formData.model.trim()) {
      setError('Modelo é obrigatório');
      return false;
    }
    if (!formData.ip_address.trim()) {
      setError('Endereço IP é obrigatório');
      return false;
    }
    if (!formData.ssh_username.trim()) {
      setError('Usuário SSH é obrigatório');
      return false;
    }
    if (!formData.ssh_password.trim()) {
      setError('Senha SSH é obrigatória');
      return false;
    }

    // Validar IP
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(formData.ip_address)) {
      setError('Formato de IP inválido');
      return false;
    }

    // Validar portas
    if (formData.ssh_port < 1 || formData.ssh_port > 65535) {
      setError('Porta SSH deve estar entre 1 e 65535');
      return false;
    }
    if (formData.snmp_port < 1 || formData.snmp_port > 65535) {
      setError('Porta SNMP deve estar entre 1 e 65535');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        vendor: formData.vendor || undefined,
        tipo_comunicacao: formData.tipo_comunicacao || 'ssh',
        porta_telnet: formData.tipo_comunicacao === 'telnet' ? formData.porta_telnet : undefined,
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined
      };

      console.log('🔄 Enviando dados para API:', submitData);

      let response;
      if (olt) {
        // Edição
        response = await oltService.update(olt.id, submitData);
      } else {
        // Criação
        response = await oltService.create(submitData);
      }

      console.log('📡 Resposta da API:', response);
      console.log('✅ Success:', response.success);
      console.log('📦 Data:', response.data);
      console.log('📞 onSuccess function:', onSuccess);

      // Verificar se a operação foi bem-sucedida
      const isSuccess = response && (
        response.success === true ||  // Wrapper format: {success: true, data: {...}}
        response.data ||              // Wrapper format with data
        ('id' in response && response.id) // Direct OLT object format: {id: 1, name: "...", ...}
      );

      console.log('✅ Operação bem-sucedida:', isSuccess);

      if (isSuccess && onSuccess) {
        console.log('📞 Chamando função de sucesso');
        // Se é um wrapper, usar response.data, senão usar response diretamente
        const oltData = response.data || response;

        // Mostrar toast de sucesso
        if (olt) {
          toast.success('OLT atualizada com sucesso!');
        } else {
          toast.success('OLT criada com sucesso!');
        }

        onSuccess(oltData);
      } else if (!isSuccess) {
        console.warn('⚠️ Operação não foi bem-sucedida ou resposta inválida');
        setError('Erro ao salvar OLT: Resposta inválida da API');
      } else if (!onSuccess) {
        console.warn('⚠️ Função onSuccess não fornecida');
        setError('Erro interno: Função de sucesso não configurada');
      }
    } catch (err: any) {
      console.error('❌ Erro ao salvar OLT:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao salvar OLT');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {olt ? 'Editar OLT' : 'Nova OLT'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Informações Básicas */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Informações Básicas
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nome da OLT"
            value={formData.name}
            onChange={handleChange('name')}
            required
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Fabricante</InputLabel>
            <Select
              value={formData.vendor}
              onChange={handleSelectChange('vendor')}
              label="Fabricante"
            >
              <MenuItem value="datacom">Datacom</MenuItem>
              <MenuItem value="fdata">Fdata</MenuItem>
              <MenuItem value="huawei">Huawei</MenuItem>
              <MenuItem value="vsolution">VSolution</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Modelo"
            value={formData.model}
            onChange={handleChange('model')}
            required
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Endereço IP"
            value={formData.ip_address}
            onChange={handleChange('ip_address')}
            required
            size="small"
            placeholder="192.168.1.1"
          />
        </Grid>

        {/* Configurações de Comunicação */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
            Configurações de Comunicação
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Tipo de Comunicação</InputLabel>
            <Select
              value={formData.tipo_comunicacao}
              onChange={handleSelectChange('tipo_comunicacao')}
              label="Tipo de Comunicação"
            >
              <MenuItem value="ssh">SSH</MenuItem>
              <MenuItem value="telnet">Telnet</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {formData.tipo_comunicacao === 'ssh' && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Porta SSH"
              type="number"
              value={formData.ssh_port}
              onChange={handleChange('ssh_port')}
              size="small"
              InputProps={{
                inputProps: { min: 1, max: 65535 }
              }}
            />
          </Grid>
        )}

        {formData.tipo_comunicacao === 'telnet' && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Porta Telnet"
              type="number"
              value={formData.porta_telnet}
              onChange={handleChange('porta_telnet')}
              size="small"
              InputProps={{
                inputProps: { min: 1, max: 65535 }
              }}
            />
          </Grid>
        )}

        {/* Configurações SSH/Telnet */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
            Configurações {formData.tipo_comunicacao === 'ssh' ? 'SSH' : 'Telnet'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={`Usuário ${formData.tipo_comunicacao === 'ssh' ? 'SSH' : 'Telnet'}`}
            value={formData.ssh_username}
            onChange={handleChange('ssh_username')}
            required
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={`Senha ${formData.tipo_comunicacao === 'ssh' ? 'SSH' : 'Telnet'}`}
            type={showPassword ? 'text' : 'password'}
            value={formData.ssh_password}
            onChange={handleChange('ssh_password')}
            required
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Senha Admin */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
            Senha Administrativa
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.solicita_senha_enable}
                onChange={handleChange('solicita_senha_enable')}
                color="primary"
              />
            }
            label='OLT Solicita senha no comando "enable"'
          />
        </Grid>

        {formData.solicita_senha_enable && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Senha Enable"
              type={showEnablePassword ? 'text' : 'password'}
              value={formData.senha_enable}
              onChange={handleChange('senha_enable')}
              size="small"
              placeholder="Senha de acesso enable"
              helperText="Senha usada para o comando enable na OLT"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEnablePassword(!showEnablePassword)}
                      edge="end"
                      size="small"
                    >
                      {showEnablePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}

        {/* Configurações SNMP */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
            Configurações SNMP
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Comunidade SNMP"
            value={formData.snmp_community}
            onChange={handleChange('snmp_community')}
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Porta SNMP"
            type="number"
            value={formData.snmp_port}
            onChange={handleChange('snmp_port')}
            size="small"
            InputProps={{
              inputProps: { min: 1, max: 65535 }
            }}
          />
        </Grid>

        {/* Informações Adicionais */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
            Informações Adicionais
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Localização"
            value={formData.location}
            onChange={handleChange('location')}
            size="small"
            placeholder="Ex: Central, Bairro Norte"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.ativo}
                onChange={handleChange('ativo')}
                color="primary"
              />
            }
            label="OLT Ativa"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descrição"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            size="small"
            placeholder="Informações adicionais sobre a OLT"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Perfil ONU Padrão"
            value={formData.onu_profile}
            onChange={handleChange('onu_profile')}
            size="small"
            placeholder="Ex: default, residential, business"
            helperText="Perfil padrão para provisionamento de ONUs"
          />
        </Grid>
      </Grid>

      {/* Botões */}
      <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {onCancel && (
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Salvando...' : (olt ? 'Atualizar' : 'Criar OLT')}
        </Button>
      </Box>
    </Box>
  );
};

export default OLTForm; 